import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from 'auth';
import { RedeemBookingService } from '../../core/services/redeem-booking.service';
import { ToastService } from '../../core/services/toast.service';
import {
  BookingStatusDisplay,
  GuestClaimResult,
  MealBooking,
} from '../../core/models/redeem-booking.models';
import { finalize } from 'rxjs';
import { ModalStep, RedeemModalMode } from '../../core/models/redeem-booking-modal.models';

@Component({
  selector: 'app-redeem-booking-modal',
  imports: [CommonModule, FormsModule],
  templateUrl: './redeem-booking-modal.html',
  styleUrl: './redeem-booking-modal.scss',
  standalone: true,
})
export class RedeemBookingModal {
  /** 'booking' (default) redeems a staff PIN via validate+confirm; 'guest' redeems a
   *  guest claim code in a single call and skips the details/preview step. */
  @Input() mode: RedeemModalMode = 'booking';
  /** Who is operating this redemption — sent as performedByName on guest claims. */
  @Input() performedByName: string | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() redeemed = new EventEmitter<MealBooking>();
  @Output() guestRedeemed = new EventEmitter<GuestClaimResult>();

  @ViewChildren('pinInput') pinInputs!: QueryList<ElementRef<HTMLInputElement>>;

  step: ModalStep = 'scan';
  pinDigits: string[] = ['', '', '', '', ''];
  isLookingUp = false;
  isConfirming = false;
  lookupError = '';

  booking: MealBooking | null = null;
  statusDisplay: BookingStatusDisplay | null = null;
  redeemedBooking: MealBooking | null = null;
  guestClaimResult: GuestClaimResult | null = null;

  get modalTitle(): string {
    return this.mode === 'guest' ? 'Redeem Guest' : 'Redeem Booking';
  }

  get submitLabel(): string {
    return this.mode === 'guest' ? 'Redeem Guest' : 'Redeem Booking';
  }

  constructor(
    private redeemBookingService: RedeemBookingService,
    private toastService: ToastService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {}

  get pinValue(): string {
    return this.pinDigits.join('');
  }

  get isPinComplete(): boolean {
    return this.pinValue.length === 5;
  }

  closeModal(): void {
    this.close.emit();
  }

  trackByIndex(index: number): number {
    return index;
  }

  onPinDigitChange(index: number, raw: string): void {
    const value = String(raw ?? '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(-1);

    this.pinDigits[index] = value;
    this.lookupError = '';

    if (value && index < 4) {
      requestAnimationFrame(() => {
        this.pinInputs?.get(index + 1)?.nativeElement?.focus();
      });
    }
  }

  onPinKeydown(index: number, event: KeyboardEvent): void {
    if (event.key !== 'Backspace') return;

    if (this.pinDigits[index]) {
      this.pinDigits[index] = '';
      event.preventDefault();
      return;
    }

    if (index > 0) {
      this.pinDigits[index - 1] = '';
      this.pinInputs?.get(index - 1)?.nativeElement?.focus();
      event.preventDefault();
    }
  }

  onPinPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') ?? '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 5);

    this.pinDigits = ['', '', '', '', ''];
    pasted.split('').forEach((char, i) => {
      this.pinDigits[i] = char;
    });
    this.lookupError = '';

    requestAnimationFrame(() => {
      const focusIndex = Math.min(pasted.length, 4);
      this.pinInputs?.get(focusIndex)?.nativeElement?.focus();
    });
  }

  submitPinLookup(): void {
    if (!this.isPinComplete || this.isLookingUp) return;

    if (this.mode === 'guest') {
      this.submitGuestClaim();
      return;
    }

    const vendorId = this.authService.vendorId();
    if (!vendorId) {
      this.toastService.error('Vendor ID not found. Please log in again.');
      return;
    }

    this.isLookingUp = true;
    this.lookupError = '';
    this.redeemBookingService
      .confirmMealClaim(vendorId, this.pinValue)
      .pipe(
        finalize(() => {
          this.isLookingUp = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (result) => {
          if (!result.booking) {
            this.lookupError = result.message;
            this.toastService.error(result.message);
            this.cdr.detectChanges();
            return;
          }

          this.redeemedBooking = result.booking;
          this.step = 'success';
          this.redeemed.emit(result.booking);
          this.toastService.success(result.message);
          this.cdr.detectChanges();
        },
      });
  }

  /** Guest claim codes are redeemed in one call — no separate preview/confirm step,
   *  so a successful response goes straight to the success screen. */
  private submitGuestClaim(): void {
    const vendorId = this.authService.vendorId();
    if (!vendorId) {
      this.toastService.error('Vendor ID not found. Please log in again.');
      return;
    }

    this.isLookingUp = true;
    this.lookupError = '';
    this.redeemBookingService
      .claimGuestMeal(this.pinValue, vendorId, this.performedByName)
      .pipe(
        finalize(() => {
          this.isLookingUp = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (result) => {
          if (!result.claim) {
            // Nothing to build a success card from — surface it inline on the
            // scan step instead of a toast, same treatment as an unrecognized
            // staff PIN.
            this.lookupError = result.message;
            this.cdr.detectChanges();
            return;
          }

          this.guestClaimResult = result.claim;
          this.step = 'success';
          this.guestRedeemed.emit(result.claim);
          this.toastService.success(result.message);
          this.cdr.detectChanges();
        },
      });
  }

  showBookingDetails(booking: MealBooking): void {
    this.booking = booking;
    this.statusDisplay = this.redeemBookingService.getStatusDisplay(
      booking.validationStatus,
      booking.redeemed,
    );
    this.step = 'details';
  }

  backToScan(): void {
    this.step = 'scan';
    this.booking = null;
    this.statusDisplay = null;
    this.guestClaimResult = null;
    this.resetPin();
    this.lookupError = '';
  }

  confirmRedemption(): void {
    if (!this.booking || !this.statusDisplay?.canConfirm || this.isConfirming) return;

    const vendorId = this.authService.vendorId();
    const claimPasscode = this.booking.pin || this.pinValue;
    if (!vendorId) {
      this.toastService.error('Vendor ID not found. Please log in again.');
      return;
    }
    if (!claimPasscode) {
      this.toastService.error('Claim passcode is missing for this booking.');
      return;
    }

    this.isConfirming = true;
    this.redeemBookingService
      .confirmMealClaim(vendorId, claimPasscode)
      .pipe(
        finalize(() => {
          this.isConfirming = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (result) => {
          if (!result.booking) {
            this.toastService.error(result.message);
            this.cdr.detectChanges();
            return;
          }

          this.redeemedBooking = result.booking;
          this.step = 'success';
          this.redeemed.emit(result.booking);
          this.toastService.success(result.message);
          this.cdr.detectChanges();
        },
      });
  }

  finishSuccess(): void {
    this.closeModal();
  }

  private resetPin(): void {
    this.pinDigits = ['', '', '', '', ''];
  }
}
